import { Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';

// Services
import { AiDocumentationAgentService } from 'src/app/@core/services/ai/ai-documentation-agent.service';

// Composants
import { BaseComponent } from '../../abstract/base.component';
import { AssistantComposerComponent } from 'src/app/ai/components/assistant-composer/assistant-composer.component';
import { AssistantConversationComponent } from 'src/app/ai/components/assistant-conversation/assistant-conversation.component';
import { DocLinkTarget } from '../documentation-content/documentation-content.component';

// Modèles
import { ConversationMessage } from 'src/app/@core/services/ai/ai-assistant.service';
import { AgentTurnFeedback } from 'src/app/@shared/models/ai/agent-turn.model';

/**
 * Ce qui nomme la question posée à l'assistant : l'écran d'où elle part, ou
 * l'article qu'on est en train de lire.
 */
export interface DocAssistantContext {
    /** Ce qu'on nomme devant la question : un écran, ou un article. */
    kind: 'screen' | 'article';
    /** Écran (`<module>.<id>`) ou identifiant d'article. */
    id: string;
    /** Libellé nommé devant la question : nom de l'écran, ou titre de l'article. */
    label: string;
    /**
     * **Ce à quoi la conversation se rapporte** — en changer en ouvre une neuve.
     * Chaque hôte dit la sienne :
     *
     * - `screen:<écran>` — le panneau d'aide ;
     * - `article:<id>` — la page d'un article ;
     * - `window:<uuid>:<id>` — la fenêtre d'article, éphémère : elle n'hérite
     *   pas de ce qu'on lisait ailleurs, et deux fenêtres successives ne
     *   partagent pas leur fil ;
     * - `manual` — l'accueil, seul endroit où la question ne nomme rien.
     *
     * La règle tient en une phrase : **la conversation est liée à ce que sa
     * première question nomme**. Le contexte n'étant posé que sur cette
     * première question, la poursuivre ailleurs ferait répondre l'assistant sur
     * l'écran ou l'article qu'on a quitté.
     */
    scope: string;
}

/** Portée par défaut : l'accueil du manuel, où la question ne nomme rien. */
const MANUAL_SCOPE = 'manual';

/** Distance du bas, en pixels, en deçà de laquelle le fil continue de suivre la réponse. */
const FOLLOW_THRESHOLD = 120;

/**
 * La conversation avec l'assistant du manuel, partout où on peut le joindre :
 * l'accueil de la documentation, le panneau d'aide d'un écran, un article et la
 * fenêtre d'article.
 *
 * Le composant porte la **plomberie** — le fil, le statut, l'envoi, le contexte
 * et le défilement — jamais l'habillage : l'en-tête, le retour et la place que
 * prend la conversation appartiennent à l'hôte, et c'est justement ce qui change
 * d'un point d'entrée à l'autre. Comme le corps de lecture d'un article, il ne
 * navigue pas sur une citation : il **émet**, l'hôte décide.
 *
 * L'hôte qui a son propre champ de saisie (la barre de recherche de l'accueil,
 * le champ du panneau d'aide) appelle `ask()` ; le champ de relance du fil passe
 * par le même chemin, pour que la règle de contexte s'applique une fois pour
 * toutes.
 */
@Component({
    selector: 'app-documentation-assistant',
    imports: [
        AssistantComposerComponent,
        AssistantConversationComponent,
    ],
    templateUrl: './documentation-assistant.component.html'
})
export class DocumentationAssistantComponent extends BaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    /** Citation du manuel suivie par le lecteur : à l'hôte de décider ce qu'il en fait. */
    @Output() openDoc = new EventEmitter<DocLinkTarget>();

    @ViewChild('thread') private _thread?: ElementRef<HTMLElement>;

    private _agent = inject(AiDocumentationAgentService);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _context: DocAssistantContext | null = null;
    public get context(): DocAssistantContext | null {
        return this._context;
    }
    /** Le contexte de l'hôte. Absent sur l'accueil : la question s'y suffit. */
    @Input() public set context(value: DocAssistantContext | null) {
        this._context = value;
    }

    /**
     * La conversation remplit la hauteur de son hôte : le fil prend la place
     * disponible et défile, le champ de saisie reste **en bas**. C'est ce qu'on
     * attend d'une fenêtre, dont la hauteur est posée d'avance — sans quoi une
     * conversation qui commence laisse le champ collé en haut et un grand vide
     * dessous. Ailleurs, la conversation fait la hauteur de son contenu.
     *
     * L'hôte doit alors être **lui-même une colonne flex** qui prend la place
     * disponible — `class="flex flex-column flex-1 h-auto min-h-0"`, cf. la
     * fenêtre d'article. Le remplissage ne passe par **aucune hauteur en
     * pourcentage** : `BaseComponent` pose `h-full` sur l'hôte de tout composant
     * qui en hérite, et les utilitaires PrimeFlex sont tous en `!important` —
     * `h-auto`, déclaré après `h-full`, l'emporte, et un `height: 100%` n'a plus
     * rien à quoi se rapporter. Une chaîne flex, elle, ne dépend d'aucune
     * hauteur déclarée.
     */
    private _fill = false;
    public get fill(): boolean {
        return this._fill;
    }
    @Input() public set fill(value: boolean) {
        this._fill = value;
    }

    private _messages: ConversationMessage[] = [];
    public get messages(): ConversationMessage[] {
        return this._messages;
    }

    private _statusText: string | null = null;
    public get statusText(): string | null {
        return this._statusText;
    }

    /** Conversation en cours : l'avis sur une réponse se dépose sur l'un de ses tours. */
    public get sessionId(): string | null {
        return this._agent.sessionId;
    }

    private _streaming = false;
    public get streaming(): boolean {
        return this._streaming;
    }

    /**
     * Classe du conteneur du fil. C'est par elle que le panneau d'aide lui donne
     * une hauteur bornée et son propre défilement ; ailleurs, le fil suit le
     * défilement de la page ou de la fenêtre.
     */
    private _threadClass = '';
    public get threadClass(): string {
        return this._threadClass;
    }
    @Input() public set threadClass(value: string) {
        this._threadClass = value;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    constructor() {
        super();

        this.subscriptions.add(
            this._agent.conversationMessages$.subscribe((messages) => {
                const follow = this._isNearBottom();
                this._messages = messages;
                setTimeout(() => this._scrollToBottom(follow));
            })
        );
        this.subscriptions.add(
            this._agent.statusText$.subscribe((status) => {
                const follow = this._isNearBottom();
                this._statusText = status;
                setTimeout(() => this._scrollToBottom(follow));
            })
        );
        this.subscriptions.add(
            this._agent.streaming$.subscribe((streaming) => this._streaming = streaming)
        );
    }

    /**
     * Le contexte s'applique **à l'ouverture**, pas seulement à l'envoi : ouvrir
     * l'assistant depuis un article après l'avoir quitté sur un écran doit
     * montrer une conversation neuve, pas le fil de l'écran précédent — qu'on
     * n'a plus sous les yeux.
     *
     * À l'ouverture et non à chaque changement de contexte : le panneau d'aide
     * reste monté quand on navigue, et sa conversation en cours doit rester
     * lisible jusqu'à ce qu'on la quitte. C'est la question suivante qui en
     * ouvrira une neuve, avec le nouvel écran.
     */
    public override ngOnInit(): void {
        super.ngOnInit();
        this._agent.useContext(this._conversationKey());
    }

    /** Pose la question, contexte compris. Appelée par l'hôte ou par le champ de relance. */
    public ask(question: string): void {
        this._send(question);
    }

    public onChangeFeedback(change: { turnIndex: number; feedback: AgentTurnFeedback | null; feedbackComment: string }): void {
        this._agent.setTurnFeedback(change.turnIndex, change.feedback, change.feedbackComment);
    }

    public onOpenDoc(target: DocLinkTarget): void {
        this.openDoc.emit(target);
    }

    public onSendText(text: string): void {
        this._send(text);
    }

    /** Repart d'une conversation vide — l'accueil du manuel ouvre un sujet neuf à chaque question. */
    public reset(): void {
        this._agent.clear();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Le contexte part **dans la question**, pas dans un champ technique : c'est
     * cette phrase que l'utilisateur relit dans son historique, et c'est elle qui
     * nomme la conversation dans ses récentes (arbitrage #968 du 2026-08-09).
     *
     * Il n'est posé que sur la **première** question d'une conversation : le
     * serveur reconstruit l'historique, le contexte reste acquis pour la suite.
     */
    private _compose(question: string): string {
        if (!this._context || this._messages.length) {
            return question;
        }
        return this._context.kind === 'screen'
            ? `Je suis sur l'écran « ${this._context.label} ». ${question}`
            : `Je lis l'article « ${this._context.label} ». ${question}`;
    }

    /** Portée de la conversation — l'hôte la déclare, cf. `DocAssistantContext`. */
    private _conversationKey(): string {
        return this._context?.scope ?? MANUAL_SCOPE;
    }

    /**
     * Était-on en bas du fil **avant** que le message n'arrive ? La mesure se
     * prend là, et pas après le rendu : un tour qui ajoute une longue bulle
     * mettrait sinon le bas hors de portée du seuil, et le fil cesserait de
     * suivre dès la première réponse.
     */
    private _isNearBottom(): boolean {
        const container = this._scrollContainer();
        if (!container) {
            return true;
        }
        return container.scrollHeight - container.scrollTop - container.clientHeight <= FOLLOW_THRESHOLD;
    }

    /**
     * Le conteneur qui défile autour du fil : le sien quand l'hôte lui en a donné
     * un (le panneau d'aide borne sa hauteur), celui de la page ou de la fenêtre
     * sinon. On le cherche au lieu de le déclarer : le même composant vit dans
     * une page, dans un popover et dans une fenêtre.
     */
    private _scrollContainer(): HTMLElement | undefined {
        let element: HTMLElement | null = this._thread?.nativeElement ?? null;
        while (element) {
            const overflow = getComputedStyle(element).overflowY;
            if ((overflow === 'auto' || overflow === 'scroll') && element.scrollHeight > element.clientHeight) {
                return element;
            }
            element = element.parentElement;
        }
        return undefined;
    }

    /**
     * On ne suit la réponse que si on était déjà en bas. Sans cette réserve, la
     * conversation ouverte **sous** un article y ramènerait de force à chaque
     * fragment reçu, alors qu'on est peut-être remonté relire le passage qui a
     * fait poser la question.
     */
    private _scrollToBottom(follow: boolean): void {
        const container = this._scrollContainer();
        if (follow && container) {
            container.scrollTop = container.scrollHeight;
        }
    }

    private _send(question: string): void {
        const text = question.trim();
        if (!text || this._streaming) {
            return;
        }
        this._agent.useContext(this._conversationKey());
        this._agent.sendText(this._compose(text));
    }
}
